import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { RootState } from 'store/store';
import { patchRequest } from 'api/apiClient';
import { hasPermission, transformNullToEmptyString } from 'utils/utils';
import { DRAWER_WIDTH } from 'constants/ui';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import ReaderForm from './tabs/ReaderForm';
import OptionsForm from './tabs/OptionsForm';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface AvigilonReadersDrawerProps {
    open: boolean;
    onClose: () => void;
    entryIdOptions: { value: number; label: string }[];
    integrationData: any;
    selectedAcu: any | null;
    selectedReaderOptions: any | null;
    setAcus: (acus: any[]) => void;
}

const AvigilonReadersDrawer: React.FC<AvigilonReadersDrawerProps> = ({ open, onClose, entryIdOptions, integrationData, selectedAcu, selectedReaderOptions, setAcus }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);

    const [activeKey, setActiveKey] = useState<string>('reader');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [form] = OpForm.useForm();

    const hasSigninConfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:w');
    
    const { t } = useTranslation();
    useEffect(() => {
        if (open) {
            setActiveKey('reader');
        }
    }, [open]);

    const initialValues = selectedAcu && selectedReaderOptions ? {
        acuId: selectedReaderOptions.acuId || "",
        name: selectedAcu.name || "",
        description: selectedAcu.notes || "",
        status: selectedAcu.statusMessage || "",
        qrEnabled: selectedReaderOptions.qrEnabled,
        entryEnabled: selectedReaderOptions.entryEnabled,
        entryId: selectedReaderOptions.entryId,
        entryMinsBefore: selectedReaderOptions.entryMinsBefore,
        entryMinsAfter: selectedReaderOptions.entryMinsAfter,
        autoSignIn: selectedReaderOptions.autoSignIn,
    } : {};

    const tabItems = [
        {
            key: 'reader',
            label: t('avigilonReader.tabs.reader'),
            children: <ReaderForm />,
        },
        {
            key: 'options',
            label: t('avigilonReader.tabs.options'),
            children: <OptionsForm entryIdOptions={entryIdOptions} />,
        },
    ];

    const handleSubmit = useCallback(async ({ touchedValues }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            const transformedValues = transformNullToEmptyString(touchedValues);
            if (touchedValues && Object.keys(touchedValues).length > 0) {
                await patchRequest(`/orgs/${orgId}/msiAltaReader/${selectedReaderOptions.id}`, transformedValues);
                console.log(transformedValues);
                notification.success({
                    message: t('notification.success'),
                    description: t('avigilonReader.notification.updateSuccess'),
                    placement: 'bottomRight',
                });

                const { msiAltaOrgId, msiAltaToken, msiAltaApiHost } = integrationData;
                const apiHost = msiAltaApiHost || 'https://api.openpath.com';

                const acus = await axios.get(`${apiHost}/orgs/${msiAltaOrgId}/acus`, {
                    headers: {
                        Authorization: msiAltaToken,
                    },
                });
                const filteredAcus = acus.data.data.filter((acu: any) => acu.acuModel && (acu.acuModel.id === 9 || acu.acuModel.id === 10));
                setAcus(filteredAcus);
            }
            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('avigilonReader.notification.saveError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [form, t, onClose, orgId, selectedReaderOptions?.id, setAcus, integrationData]);

    return (
        <OpFormDrawer
            form={form}
            title={selectedAcu?.name}
            width={DRAWER_WIDTH}
            open={open}
            onClose={onClose}
            isFormLoading={isSubmitting}
            isFormReadOnly={!hasSigninConfWrite}
            formComponent={
                <OpForm
                    form={form}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    hasError={false}
                    defaultButtons={false}
                    isReadOnly={!hasSigninConfWrite}
                >
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                </OpForm>
            }
        />
    );
};

export default AvigilonReadersDrawer;
